import React, { Component } from "react";
import { Link } from "react-router-dom";
import configuration from "react-global-configuration";
import Helper from "../../components/Helper/helper";
import ContentLoader from "../../components/Static/contentLoader";
import api from "../../Environment";
import {
  resolvePlaybackExperience,
  getPrimaryIntroLayer,
} from "../../components/Helper/playbackExperience";
import NextGenPlayer from "../Components/NextGenPlayer";
import CompanionPresentationSurface from "../tpg/CompanionPresentationSurface";
import { PlayerEvent } from "../Core/PlayerEvents";
import { createSubtitlesPlugin } from "../Plugins/builtins/subtitlesPlugin";
import { createAudioTracksPlugin } from "../Plugins/builtins/audioTracksPlugin";
import { createResumePlaybackPlugin } from "../Plugins/builtins/resumePlaybackPlugin";
import { createHostAnalyticsPlugin } from "../Plugins/builtins/hostAnalyticsPlugin";
import { createContinueWatchingPlugin } from "../Plugins/builtins/continueWatchingPlugin";
import { createWatchHistoryPlugin } from "../Plugins/builtins/watchHistoryPlugin";
import { createExperienceBridgePlugin } from "../Plugins/builtins/experienceBridgePlugin";
import { createModernAvodPlugin } from "../Advertising/createModernAvodPlugin";
import { resolveModernVideoAdsFlags } from "../Advertising/modernVideoAdsFlags";
import { fetchAvodPlan } from "../../components/Advertising/advertisingApi";
import { createLocalProgressStorage } from "../Services/ProgressStorage";
import {
  buildHostPlayerConfig,
  persistVolume,
  resolveMediaFromVideoDetails,
  resolvePlayableSrc,
  readPersistedMuted,
  readPersistedVolume,
  siteFeatures,
} from "./buildHostPlayerConfig";
import { resolveTheaterMode } from "../Services/playerPreferences";
import {
  hasTransientUserActivation,
  isReloadNavigation,
  shouldForceMutedIntro,
  armUnmuteOnNextUserGesture,
  applyUnmuteToEngine,
  markPlaybackReloadHint,
  getNavigationTimingKind,
} from "./autoplayPolicy";
import { createHostPlaybackSession } from "./createHostPlaybackSession";
import { createPlaybackPipeline, PipelinePhase } from "../Core/PlaybackPipeline";
import { createPlaybackTimeline } from "../Core/PlaybackTimeline";
import { createPlaybackStateService } from "../Core/PlaybackStateService";
import { PlaybackPlatformEvent } from "../Core/PlaybackPlatformEvents";
import {
  CINEMATIC_HANDOFF_CLASS,
  planIntroToMainHandoff,
  clearCinematicHandoff,
  cinematicIntroPlaybackOpts,
} from "./cinematicHandoff";
import {
  resolveHostControlPlane,
  controlPlaneCacheKey,
} from "./resolveHostControlPlane";
import { tpgTrace, tpgTraceDump } from "./tpgPipelineTrace";
import "../Themes/player.css";
import "./HostPlaybackPage.css";

/**
 * Production host for TPG Player.
 * Phase B3: Media Asset control plane drives strategy, capabilities, and Facade.
 *
 * NOTE: [TPG] pipeline instrumentation is active for Phase 2 production diagnosis.
 */
class HostPlaybackPage extends Helper {
  constructor(props) {
    super(props);
    this._avodPlan = null;
    this._avodPlanFetched = false;
    tpgTrace("PAGE_LOADED", {
      href:
        typeof window !== "undefined" && window.location
          ? window.location.href
          : null,
      navType:
        typeof performance !== "undefined" &&
        performance.getEntriesByType &&
        performance.getEntriesByType("navigation")[0]
          ? performance.getEntriesByType("navigation")[0].type
          : null,
      hasRouteState: !!(
        props.location &&
        props.location.state &&
        props.location.state.videoDetailsFirst
      ),
    });
    this.state = {
      loadingFirst: true,
      videoDetailsFirst: null,
      videoDetailsError: null,
      onPlayStarted: false,
      historyRecorded: false,
      playingCinematicIntro: false,
      introSkipVisible: false,
      cinematicIntroChecked: false,
      cinematicHandoff: false,
      forceMutedMainAutoplay: false,
      sessionAudioUnlocked: false,
      playerKey: 0,
      engineLabel: "nextgen",
      loadError: null,
      theaterMode: resolveTheaterMode(siteFeatures().theaterDefault),
    };
    this._api = null;
    this._engine = null;
    this._session = null;
    this._timeline = null;
    this._stateService = null;
    this._volumeUnsub = null;
    this._pipeline = createPlaybackPipeline();
    this._controlPlane = null;
    this._pluginsCacheKey = null;
    this._cachedPlugins = null;
    this._introHandoffStarted = false;
    this._lastPlayerMode = null;
    this._stageEl = null;
    this._disposeHostUnmuteArm = null;
    this._sessionAudioUnlocked = false;
    this.handleReady = this.handleReady.bind(this);
    this.handlePlaybackEvent = this.handlePlaybackEvent.bind(this);
    this.handleEnded = this.handleEnded.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleTheaterChange = this.handleTheaterChange.bind(this);
    this.skipCinematicIntro = this.skipCinematicIntro.bind(this);
    this.handleAudioUnlocked = this.handleAudioUnlocked.bind(this);
    this.handleNeedGestureUnmute = this.handleNeedGestureUnmute.bind(this);
    this._setStageRef = this._setStageRef.bind(this);
    this._getStateService = this._getStateService.bind(this);
    this._recordHistory = this._recordHistory.bind(this);
  }

  _setStageRef(el) {
    this._stageEl = el;
  }

  /**
   * Session-wide audio unlock — survives intro→main remount.
   * Persists unmuted so later configs do not re-apply policy mute from localStorage.
   * Must unmute the *current* player (intro or main) immediately.
   */
  handleAudioUnlocked() {
    if (this._disposeHostUnmuteArm) {
      this._disposeHostUnmuteArm();
      this._disposeHostUnmuteArm = null;
    }
    this._sessionAudioUnlocked = true;
    var provider =
      (this._engine &&
        typeof this._engine.getProviderName === "function" &&
        this._engine.getProviderName()) ||
      (this._api &&
        this._api.engine &&
        typeof this._api.engine.getProviderName === "function" &&
        this._api.engine.getProviderName()) ||
      null;
    var duringIntro = !!this.state.playingCinematicIntro;
    tpgTrace("SESSION_AUDIO_UNLOCKED", {
      playerKey: this.state.playerKey,
      provider: provider,
      playingCinematicIntro: duringIntro,
    });
    tpgTrace("HOST_AUDIO_STATE", {
      sessionAudioUnlocked: true,
      forceMutedMainAutoplay: false,
      duringIntro: duringIntro,
    });
    if (!this.state.sessionAudioUnlocked) {
      this.setState({ sessionAudioUnlocked: true, forceMutedMainAutoplay: false });
    }
    try {
      persistVolume(readPersistedVolume(), false);
    } catch (e) {
      /* ignore */
    }

    // Prefer engine.setMuted — onReady's api bag does not expose .engine.
    // MUST run synchronously inside the user-gesture stack (armUnmute is sync).
    var unmuted = false;
    var vol = readPersistedVolume() || 1;
    try {
      if (duringIntro) {
        tpgTrace("INTRO_PLAYER_SET_MUTED", { muted: false, provider: provider });
        tpgTrace("INTRO_PLAYER_SET_VOLUME", { volume: vol, provider: provider });
      }
      if (this._engine) {
        applyUnmuteToEngine(this._engine, vol);
        unmuted = true;
      } else if (this._api && typeof this._api.setMuted === "function") {
        this._api.setMuted(false);
        if (typeof this._api.setVolume === "function") {
          this._api.setVolume(vol);
        }
        unmuted = true;
      }
      var videoEl =
        this._engine && typeof this._engine.getVideoElement === "function"
          ? this._engine.getVideoElement()
          : null;
      tpgTrace("INTRO_VIDEO_ELEMENT", {
        muted: videoEl ? !!videoEl.muted : null,
        volume: videoEl ? videoEl.volume : null,
        defaultMuted: videoEl ? !!videoEl.defaultMuted : null,
        duringIntro: duringIntro,
      });
      tpgTrace("INTRO_PROVIDER_STATE", {
        engineMuted:
          this._engine && this._engine.getState
            ? this._engine.getState().muted
            : null,
        provider: provider,
        unmuted: unmuted,
      });
      tpgTrace(duringIntro ? "INTRO_PROVIDER_UNMUTED" : "PROVIDER_UNMUTED", {
        provider: provider,
        via: "handleAudioUnlocked",
        unmuted: unmuted,
        videoMuted: videoEl ? !!videoEl.muted : null,
      });
    } catch (e2) {
      tpgTrace("ERROR", {
        message: "audio_unlock_failed",
        err: e2 && e2.message,
      });
    }
  }

  /**
   * Arm unmute on the host stage (Skip Intro + chrome outside the player shell).
   */
  handleNeedGestureUnmute() {
    var self = this;
    if (this._sessionAudioUnlocked || this.state.sessionAudioUnlocked) {
      this.handleAudioUnlocked();
      return;
    }
    if (this._disposeHostUnmuteArm) {
      this._disposeHostUnmuteArm();
      this._disposeHostUnmuteArm = null;
    }
    var root = this._stageEl;
    if (!root) {
      return;
    }
    this._disposeHostUnmuteArm = armUnmuteOnNextUserGesture(root, function () {
      self.handleAudioUnlocked();
    });
  }

  _getStateService() {
    return this._stateService;
  }

  _recordHistory(contentId) {
    if (!contentId) return;
    api
      .postMethod("addHistory", { admin_video_id: contentId })
      .then(function () {})
      .catch(function () {});
    if (!this.state.historyRecorded) {
      this.setState({ historyRecorded: true });
    }
  }

  componentDidMount() {
    tpgTrace("HOST_INITIALIZED", {
      path: "componentDidMount",
      routeId:
        this.props.match && this.props.match.params
          ? this.props.match.params.id
          : null,
    });

    // Reload / hard-refresh: never inherit sticky ngp_muted from a prior
    // mute-retry into a path that intended unmuted. Browser mute-retry may
    // still mute after an unmuted play() rejection.
    // Re-check here: first render may have seen inconclusive Navigation Timing
    // before the entry/hint was available; forceUpdate rebuilds intro config.
    if (isReloadNavigation()) {
      try {
        persistVolume(readPersistedVolume(), false);
      } catch (ePersist) {
        /* ignore */
      }
      tpgTrace("STATE", {
        message: "reload_cleared_persisted_mute",
        applicationRequestedMuted: false,
        persistedMutedAfter: readPersistedMuted(),
      });
      this.forceUpdate();
    }
    this._onPageHideMarkReload = function () {
      markPlaybackReloadHint();
    };
    if (typeof window !== "undefined") {
      // pagehide + beforeunload: some WebViews omit one; hint covers
      // refresh when Navigation Timing mis-reports type as navigate.
      window.addEventListener("pagehide", this._onPageHideMarkReload);
      window.addEventListener("beforeunload", this._onPageHideMarkReload);
    }

    // Fetch modern AVOD / schedule plan (defaults remain OFF until dual opt-in).
    if (!this._avodPlanFetched) {
      this._avodPlanFetched = true;
      var self = this;
      fetchAvodPlan({
        content_id:
          (this.props.match &&
            this.props.match.params &&
            this.props.match.params.id) ||
          null,
      }).then(function (plan) {
        self._avodPlan = plan;
        self._pluginsCacheKey = null;
        self._cachedPlugins = null;
        if (plan && plan.avod && plan.avod.modern && plan.avod.modern.enabled) {
          self.forceUpdate();
        }
      });
    }

    var routeState =
      this.props.location && this.props.location.state
        ? this.props.location.state
        : null;
    var details = routeState && routeState.videoDetailsFirst;
    var isTrailer = !!(routeState && routeState.videoFrom === "trailer");

    if (details && details.admin_video_id && details.success !== false) {
      tpgTrace("STATE", {
        message: "init_via_route_state",
        contentId: details.admin_video_id,
        videoType: details.video_type,
      });
      this._applyVideoDetails(details, isTrailer);
      return;
    }

    // Deep link / refresh: fetch by route :id instead of bouncing to /home
    var routeId =
      this.props.match && this.props.match.params
        ? this.props.match.params.id
        : null;
    if (!routeId) {
      tpgTrace("ERROR", { message: "no_route_id_redirect_home" });
      window.location = "/home";
      return;
    }
    tpgTrace("STATE", {
      message: "init_via_fetch_refresh",
      routeId: routeId,
      isTrailer: isTrailer,
    });
    this._loadVideoById(routeId, isTrailer);
  }

  _applyVideoDetails(details, isTrailer) {
    var videoFrom = isTrailer ? "trailer" : undefined;
    this._controlPlane = resolveHostControlPlane(details, {
      videoFrom: videoFrom,
      isTrailer: isTrailer,
      siteFeatures: siteFeatures(),
    });
    this._pluginsCacheKey = null;
    this._cachedPlugins = null;

    var plane = this._controlPlane;
    var layer = plane.introLayer;
    var media = plane.mediaMeta;

    this._pipeline.beginPlayRequest({ contentId: details.admin_video_id });
    this._pipeline.transition(PipelinePhase.AUTHORIZATION);
    this._pipeline.transition(PipelinePhase.EXPERIENCE);

    this._session = createHostPlaybackSession(details, {
      isTrailer: isTrailer,
      redirectStatus: "ok",
    });
    this._timeline = createPlaybackTimeline();
    this._timeline.seedFromSession(this._session);
    this._stateService = createPlaybackStateService({
      session: this._session,
      timeline: this._timeline,
      pipeline: this._pipeline,
    });
    this._pipeline.transition(PipelinePhase.SESSION_INIT);

    var showingIntro = !!(layer && layer.url && !isTrailer);
    this._introHandoffStarted = false;
    if (showingIntro) {
      this._pipeline.transition(PipelinePhase.CINEMATIC_INTRO);
      this._session.setIntroActive(true, {
        url: layer.url,
        layerId: layer.id || layer.key || null,
      });
    } else {
      this._pipeline.transition(PipelinePhase.MEDIA_PLAYBACK);
    }

    var playableSrc = resolvePlayableSrc(media, plane.mediaAsset);
    tpgTrace("MEDIA_ASSET_RESOLVED", {
      contentId: details.admin_video_id,
      presentationKind:
        plane.mediaAsset && plane.mediaAsset.presentationKind,
      sourceType: plane.mediaAsset && plane.mediaAsset.sourceType,
      strategyId: plane.strategy && plane.strategy.id,
      mediaMetaSrc: media && media.src,
      playableSrc: playableSrc,
      introUrl: layer && layer.url,
      showingIntro: showingIntro,
      videoType: details.video_type,
    });
    tpgTrace("CONTROL_PLANE_BUILT", {
      strategyId: plane.strategy && plane.strategy.id,
      capabilities: (plane.enabledCapabilities || []).slice(0, 12),
      showHostTitle: plane.showHostTitle,
    });
    this.setState({
      loadingFirst: false,
      videoDetailsFirst: details,
      playingCinematicIntro: showingIntro,
      cinematicIntroChecked: true,
      loadError:
        playableSrc || showingIntro
          ? null
          : "No playable media URL returned",
      controlPlaneKind:
        (plane.mediaAsset && plane.mediaAsset.presentationKind) || null,
    });
    tpgTrace("STATE", {
      message: "host_state_after_apply",
      playingCinematicIntro: showingIntro,
      hasPlayableSrc: !!(playableSrc || showingIntro),
    });
  }

  _loadVideoById(adminVideoId, isTrailer) {
    var self = this;
    this.onlySingleVideoFirst({ admin_video_id: adminVideoId }).then(function (
      details
    ) {
      if (!details) {
        self.setState({
          loadingFirst: false,
          loadError:
            (self.state.videoDetailsError &&
              (self.state.videoDetailsError.error_messages ||
                self.state.videoDetailsError.error)) ||
            "Unable to load video",
        });
        return;
      }
      // Gate unpaid content before mounting the player
      var gate = self.redirectStatus(details);
      if (gate === "ppv" || gate === "payment" || gate === "subscription") {
        self._session = createHostPlaybackSession(details, {
          isTrailer: isTrailer,
          redirectStatus: gate,
        });
        self._pipeline.transition(PipelinePhase.AUTHORIZATION, { gate: gate });
        if (self._session) {
          self._session.update("pipelinePhase", PipelinePhase.AUTHORIZATION);
        }
        self.setState({ loadingFirst: false, videoDetailsFirst: details });
        return;
      }
      self._applyVideoDetails(details, isTrailer);
    });
  }

  componentWillUnmount() {
    if (this._onPageHideMarkReload && typeof window !== "undefined") {
      window.removeEventListener("pagehide", this._onPageHideMarkReload);
      window.removeEventListener("beforeunload", this._onPageHideMarkReload);
      this._onPageHideMarkReload = null;
    }
    if (this._disposeHostUnmuteArm) {
      this._disposeHostUnmuteArm();
      this._disposeHostUnmuteArm = null;
    }
    if (typeof this._volumeUnsub === "function") {
      this._volumeUnsub();
      this._volumeUnsub = null;
    }
    // True session end (Host leave) — plugins skip session_end during cinematic remount
    if (
      this._session &&
      typeof window !== "undefined" &&
      typeof window.__NGP_ANALYTICS_SINK__ === "function"
    ) {
      try {
        window.__NGP_ANALYTICS_SINK__({
          type: "session_end",
          payload: null,
          session: {
            sessionId: this._session.getId(),
            startedAt:
              (this._session.snapshot().analytics || {}).sessionStartedAt ||
              null,
          },
        });
      } catch (e) {
        /* isolate */
      }
    }
    if (this._stateService) {
      this._stateService.destroy();
      this._stateService = null;
    }
  }

  beginMainContentPlayback = function () {
    tpgTrace("HANDOFF_STARTED", {
      introHandoffStarted: this._introHandoffStarted,
      playingCinematicIntro: this.state.playingCinematicIntro,
      playerKey: this.state.playerKey,
    });
    if (this._introHandoffStarted) {
      tpgTrace("STATE", { message: "handoff_blocked_already_started" });
      return;
    }
    if (!this.state.playingCinematicIntro) {
      tpgTrace("STATE", {
        message: "handoff_blocked_intro_flag_false",
      });
      return;
    }
    this._introHandoffStarted = true;
    var self = this;
    try {
      this._pipeline.transition(PipelinePhase.MEDIA_PLAYBACK);
    } catch (e) {
      /* isolate */
    }
    tpgTrace("INTRO_ENDED", {
      reason: "handoff_commit",
      nextPlayerKey: (this.state.playerKey || 0) + 1,
    });
    this.setState(function (prev) {
      var merged = Object.assign({}, prev, {
        sessionAudioUnlocked:
          self._sessionAudioUnlocked || !!prev.sessionAudioUnlocked,
      });
      var next = planIntroToMainHandoff(merged, self._session);
      tpgTrace("STATE", {
        message: "handoff_setState",
        prevKey: prev.playerKey,
        nextKey: next.playerKey,
        forceMutedMainAutoplay: next.forceMutedMainAutoplay,
        sessionAudioUnlocked: next.sessionAudioUnlocked,
      });
      return next;
    });
  };

  skipCinematicIntro(event) {
    if (event) event.preventDefault();
    tpgTrace("STATE", { message: "skip_intro_clicked" });
    // Skip is a legitimate user activation — unlock audio for the session.
    this.handleAudioUnlocked();
    if (this._session && typeof this._session.update === "function") {
      // Publish skip before handoff
      try {
        var bus =
          this._api && this._api.engine && this._api.engine.getEventBus
            ? this._api.engine.getEventBus()
            : null;
        if (bus && this._session.bindBus) {
          this._session.bindBus(bus);
        }
        if (bus && bus.publish) {
          bus.publish(
            PlaybackPlatformEvent.INTRO_SKIPPED,
            this._session.snapshot()
          );
        }
      } catch (e) {
        /* ignore */
      }
    }
    this.beginMainContentPlayback();
  }

  handleReady(info) {
    this._api = info && info.api;
    this._engine = info && info.engine;
    tpgTrace("STATE", {
      message: "player_ready",
      provider: info && info.provider,
      playingCinematicIntro: this.state.playingCinematicIntro,
      cinematicHandoff: this.state.cinematicHandoff,
      playerKey: this.state.playerKey,
    });
    if (this.state.playingCinematicIntro) {
      tpgTrace("INTRO_STARTED", {
        provider: info && info.provider,
        playerKey: this.state.playerKey,
      });
      tpgTrace("INTRO_PROVIDER_READY", {
        provider: info && info.provider,
        muted:
          info && info.engine && info.engine.getState
            ? info.engine.getState().muted
            : null,
      });
      tpgTrace("INTRO_ACTIVE_SURFACE", {
        provider: info && info.provider,
        surfaceKind:
          info && info.engine && info.engine.getState
            ? info.engine.getState().surfaceKind
            : null,
      });
    } else if (this.state.cinematicHandoff || this._introHandoffStarted) {
      tpgTrace("PLAYER_REMOUNTED", {
        provider: info && info.provider,
        playerKey: this.state.playerKey,
        mode: "main",
      });
      tpgTrace("MAIN_ACTIVE_SURFACE", {
        provider: info && info.provider,
        surfaceKind:
          info && info.engine && info.engine.getState
            ? info.engine.getState().surfaceKind
            : null,
        sessionAudioUnlocked: this._sessionAudioUnlocked,
      });
    }
    if (this._stateService && info && info.engine) {
      this._stateService.attachEngine(info.engine);
      this._pipeline.transition(PipelinePhase.PROVIDER_READY);
      if (!this.state.playingCinematicIntro) {
        this._pipeline.transition(PipelinePhase.MEDIA_PLAYBACK);
      }
    }
    // Bind session bus as soon as engine is ready (Experience ↔ Player cohesion).
    try {
      var bus =
        info && info.engine && info.engine.getEventBus
          ? info.engine.getEventBus()
          : null;
      if (bus && this._session && this._session.bindBus) {
        this._session.bindBus(bus);
        if (this.state.playingCinematicIntro && bus.publish) {
          bus.publish(
            PlaybackPlatformEvent.INTRO_STARTED,
            this._session.snapshot()
          );
        }
      }
      if (info && info.facade && this._controlPlane) {
        info.facade.setMediaAsset(
          this._controlPlane.mediaAsset,
          this._controlPlane.strategy
        );
        info.facade.setEnabledCapabilities(
          this._controlPlane.enabledCapabilities
        );
      }
    } catch (e) {
      /* isolate */
    }
    if (this.state.cinematicHandoff) {
      this.setState(clearCinematicHandoff);
      tpgTrace("HANDOFF_COMPLETE", {
        provider: info && info.provider,
        playerKey: this.state.playerKey,
        sessionAudioUnlocked: this._sessionAudioUnlocked,
        engineMuted:
          info && info.engine && info.engine.getState
            ? info.engine.getState().muted
            : null,
      });
    }
    if (
      this._sessionAudioUnlocked &&
      info &&
      info.engine &&
      typeof info.engine.setMuted === "function"
    ) {
      try {
        applyUnmuteToEngine(info.engine, readPersistedVolume() || 1);
      } catch (eUnlock) {
        /* ignore */
      }
    }
    if (!this.state.playingCinematicIntro) {
      var mainVideo =
        info &&
        info.engine &&
        typeof info.engine.getVideoElement === "function"
          ? info.engine.getVideoElement()
          : null;
      tpgTrace("MAIN_ACTIVE_VIDEO_ELEMENT", {
        connected: !!(mainVideo && mainVideo.isConnected),
        src: mainVideo
          ? String(mainVideo.currentSrc || mainVideo.src || "").slice(0, 80)
          : null,
        display: mainVideo && mainVideo.style ? mainVideo.style.display : null,
        provider: info && info.provider,
      });
      tpgTrace("PLAYER_REMOUNT_COMPLETE", {
        provider: info && info.provider,
        surfaceKind:
          info && info.engine && info.engine.getState
            ? info.engine.getState().surfaceKind
            : null,
        videoConnected: !!(mainVideo && mainVideo.isConnected),
        sessionAudioUnlocked: this._sessionAudioUnlocked,
      });
    } else {
      var introVideo =
        info &&
        info.engine &&
        typeof info.engine.getVideoElement === "function"
          ? info.engine.getVideoElement()
          : null;
      tpgTrace("INTRO_PLAYER_READY", {
        provider: info && info.provider,
        muted:
          info && info.engine && info.engine.getState
            ? info.engine.getState().muted
            : null,
      });
      tpgTrace("INTRO_ACTIVE_VIDEO_ELEMENT", {
        connected: !!(introVideo && introVideo.isConnected),
        muted: introVideo ? !!introVideo.muted : null,
        src: introVideo
          ? String(introVideo.currentSrc || introVideo.src || "").slice(0, 80)
          : null,
      });
      tpgTrace("INTRO_PLAYER_CREATED", {
        playerKey: this.state.playerKey,
        provider: info && info.provider,
      });
    }
    if (typeof this._volumeUnsub === "function") {
      this._volumeUnsub();
      this._volumeUnsub = null;
    }
    if (info && info.engine && typeof info.engine.on === "function") {
      var self = this;
      this._volumeUnsub = info.engine.on("volumechange", function () {
        var st = info.engine.getState();
        // Do not persist policy mute as a user preference — that locks audio
        // muted across intro→main remount via readPersistedMuted().
        if (self._sessionAudioUnlocked || self.state.sessionAudioUnlocked || !st.muted) {
          if (!st.muted && !self._sessionAudioUnlocked) {
            self.handleAudioUnlocked();
            return;
          }
          persistVolume(st.volume, st.muted);
        } else {
          persistVolume(st.volume, null);
        }
      });
      // Re-arm host unmute after main remount when still policy-muted
      if (
        !this._sessionAudioUnlocked &&
        info.engine.getState &&
        info.engine.getState().muted
      ) {
        this.handleNeedGestureUnmute();
      }
    }
    if (this.props.onPlayerReady) {
      this.props.onPlayerReady(
        Object.assign({}, info, {
          umxp: this._stateService ? this._stateService.getState() : null,
          controlPlane: this._controlPlane,
        })
      );
    }
  }

  handlePlaybackEvent(name, payload) {
    if (name === PlayerEvent.PLAYBACK_STARTED || name === "play") {
      tpgTrace("PLAYBACK_STARTED", {
        name: name,
        playingCinematicIntro: this.state.playingCinematicIntro,
        onPlayStarted: this.state.onPlayStarted,
      });
      if (!this.state.onPlayStarted && !this.state.playingCinematicIntro) {
        this._onMainPlay();
      }
    }
    if (name === PlayerEvent.PLAYBACK_ENDED || name === "ended") {
      tpgTrace("STATE", {
        message: "playback_ended_event",
        playingCinematicIntro: this.state.playingCinematicIntro,
      });
    }
    // Forward for host overlays / engagement / recommendations
    if (typeof this.props.onPlaybackEvent === "function") {
      this.props.onPlaybackEvent(name, payload, {
        sessionId: this._session ? this._session.getId() : null,
        state: this._stateService ? this._stateService.getState() : null,
      });
    }
  }

  _onMainPlay() {
    var self = this;
    if (this.state.playingCinematicIntro || this.state.onPlayStarted) {
      return;
    }
    var details =
      this.state.videoDetailsFirst ||
      (this.props.location.state && this.props.location.state.videoDetailsFirst);
    if (!details) return;

    var isTrailer =
      this.props.location.state &&
      this.props.location.state.videoFrom === "trailer";

    this.setState({ onPlayStarted: true });

    // Match legacy freeplayer: trailers do not re-check PPV gates
    if (isTrailer) {
      return;
    }

    // Entitlement re-check — consume backend only (no UMXP business logic)
    this.onlySingleVideoFirst({ admin_video_id: details.admin_video_id }).then(
      function (fresh) {
        if (fresh) {
          self.redirectStatus(fresh);
        }
      }
    );
  }

  handleEnded() {
    tpgTrace("STATE", {
      message: "handleEnded_called",
      playingCinematicIntro: this.state.playingCinematicIntro,
      introHandoffStarted: this._introHandoffStarted,
    });
    if (this.state.playingCinematicIntro) {
      this.beginMainContentPlayback();
      return;
    }
    this._pipeline.transition(PipelinePhase.COMPLETION);
    this._pipeline.transition(PipelinePhase.POST_PLAYBACK);
    this.setState({ onPlayStarted: false });
    tpgTraceDump();
  }

  handleError(err) {
    tpgTrace("ERROR", {
      message: (err && (err.message || err.code)) || "Playback failed",
      err: err,
    });
    if (typeof console !== "undefined" && console.error) {
      console.error("[HostPlaybackPage]", err);
    }
    var message =
      (err && (err.message || err.code)) || "Playback failed";
    this.setState({ loadError: String(message) });
  }

  handleTheaterChange(enabled) {
    this.setState({ theaterMode: !!enabled });
  }

  _buildPlugins(details, isTrailer, showingIntro, sessionId, sessionStartedAt) {
    var cacheKey = controlPlaneCacheKey(this._controlPlane || {}, {
      showingIntro: showingIntro,
      isTrailer: isTrailer,
      sessionId: sessionId || "",
    });
    if (this._pluginsCacheKey === cacheKey && this._cachedPlugins) {
      return this._cachedPlugins;
    }

    var plugins = [
      createSubtitlesPlugin(),
      createAudioTracksPlugin(),
      createHostAnalyticsPlugin({
        sessionId: sessionId,
        sessionStartedAt: sessionStartedAt,
        continuousSession: true,
        sink: function (evt) {
          if (
            typeof window !== "undefined" &&
            window.__NGP_ANALYTICS_SINK__
          ) {
            window.__NGP_ANALYTICS_SINK__(evt);
          }
        },
      }),
      createExperienceBridgePlugin({
        getStateService: this._getStateService,
      }),
    ];

    if (!isTrailer && !showingIntro) {
      plugins.push(
        createWatchHistoryPlugin({
          getStateService: this._getStateService,
          recordHistory: this._recordHistory,
        }),
        createContinueWatchingPlugin({
          getStateService: this._getStateService,
        })
      );
      if (!(Number(details.seek_time_in_seconds) > 0)) {
        var profileId =
          (typeof localStorage !== "undefined" &&
            localStorage.getItem("active_profile_id")) ||
          "default";
        plugins.push(
          createResumePlaybackPlugin({
            contentId: String(details.admin_video_id),
            storage: createLocalProgressStorage({
              prefix: "ngp.progress." + profileId + ".",
            }),
            saveIntervalMs: 5000,
            autoResume: true,
            minResumeSeconds: 5,
            getStateService: this._getStateService,
          })
        );
      } else if (this._timeline && Number(details.seek_time_in_seconds) > 0) {
        this._timeline.addMarker({
          id: "resume",
          type: "resume_position",
          start: Number(details.seek_time_in_seconds),
          meta: { source: "server" },
        });
      }

      // Modern IMA/VAST/VMAP — dual opt-in only (BE modern.enabled + FE modernImaAds).
      // Default path: flags resolve disabled → plugin is not attached.
      var avodPayload =
        (this._avodPlan && this._avodPlan.avod) || this._avodPlan || null;
      var schedulePayload =
        (this._avodPlan && this._avodPlan.schedule) ||
        (avodPayload && avodPayload.schedule) ||
        null;
      var features = siteFeatures();
      var modernFlags = resolveModernVideoAdsFlags({
        features: features,
        avod: avodPayload || {},
      });
      if (modernFlags.enabled) {
        plugins.push(
          createModernAvodPlugin({
            features: features,
            avod: avodPayload || { modern: { enabled: true } },
            schedule: schedulePayload || avodPayload || {},
            contentId: details && details.admin_video_id
              ? String(details.admin_video_id)
              : null,
            isLive: !!(details && (details.is_live || details.live)),
          })
        );
      }
    }

    this._pluginsCacheKey = cacheKey;
    this._cachedPlugins = plugins;
    return plugins;
  }

  buildConfig() {
    var details =
      this.state.videoDetailsFirst ||
      (this.props.location.state &&
        this.props.location.state.videoDetailsFirst);
    var videoFrom =
      this.props.location.state && this.props.location.state.videoFrom;
    var isTrailer = videoFrom === "trailer";

    if (!this._controlPlane && details) {
      this._controlPlane = resolveHostControlPlane(details, {
        videoFrom: videoFrom,
        isTrailer: isTrailer,
        siteFeatures: siteFeatures(),
      });
    }
    var plane = this._controlPlane;
    var introLayer = plane && plane.introLayer;
    var showingIntro =
      this.state.playingCinematicIntro && introLayer && introLayer.url;

    var sessionId = this._session ? this._session.getId() : null;
    var sessionStartedAt =
      this._session && this._session.snapshot
        ? (this._session.snapshot().analytics || {}).sessionStartedAt
        : null;

    var plugins = this._buildPlugins(
      details,
      isTrailer,
      showingIntro,
      sessionId,
      sessionStartedAt
    );

    if (showingIntro) {
      var hasRouteVideoState = !!(
        this.props.location &&
        this.props.location.state &&
        this.props.location.state.videoDetailsFirst
      );
      var isReload = isReloadNavigation();
      var navigationTimingKind = getNavigationTimingKind();
      var introOpts = cinematicIntroPlaybackOpts(function () {
        return shouldForceMutedIntro({
          hasRouteVideoState: hasRouteVideoState,
          isReload: isReload,
          navigationTimingKind: navigationTimingKind,
        });
      });
      // Final intro mute request: never force mute solely because of reload.
      // Hard refresh clears location.state (hasRouteVideoState=false); if reload
      // detection misses / Timing is inconclusive, policy must not treat this
      // as a cold deep link. Always prefer unmuted on reload; mute-retry if blocked.
      var timingInconclusive =
        navigationTimingKind == null || navigationTimingKind === "";
      var introMutedRequest =
        this._sessionAudioUnlocked || this.state.sessionAudioUnlocked
          ? false
          : isReload || (timingInconclusive && !hasRouteVideoState)
            ? false
            : !!introOpts.muted;
      // Distinguish application mute REQUEST from later browser mute-retry.
      tpgTrace("INTRO_MUTE_REQUEST", {
        applicationRequestedMuted: !!introMutedRequest,
        source: introMutedRequest
          ? "autoplay_policy_cold_or_no_activation"
          : isReload
            ? "reload_prefer_unmuted"
            : timingInconclusive && !hasRouteVideoState
              ? "inconclusive_timing_prefer_unmuted"
              : this._sessionAudioUnlocked || this.state.sessionAudioUnlocked
                ? "session_audio_unlocked"
                : "prefer_unmuted",
        isReload: isReload,
        hasRouteVideoState: hasRouteVideoState,
        navigationTimingKind: navigationTimingKind,
        policyMuted: introOpts.muted,
        persistedMuted: readPersistedMuted(),
      });
      if (introMutedRequest) {
        tpgTrace("INTRO_MUTED", {
          reason: "application_requested_muted",
          isReload: isReload,
          navigationTimingKind: navigationTimingKind,
          hasUserActivation: hasTransientUserActivation(),
        });
      }
      tpgTrace("STATE", {
        message: "intro_autoplay_policy",
        shouldMute: introMutedRequest,
        applicationRequestedMuted: !!introMutedRequest,
        policyMuted: introOpts.muted,
        hasUserActivation: hasTransientUserActivation(),
        isReload: isReload,
        hasRouteVideoState: hasRouteVideoState,
        navigationTimingKind: navigationTimingKind,
        persistedMuted: readPersistedMuted(),
        sessionAudioUnlocked: this._sessionAudioUnlocked,
        navType: navigationTimingKind,
      });
      return buildHostPlayerConfig(details, {
        videoFrom: videoFrom,
        isTrailer: isTrailer,
        applySeek: false,
        autoplay: introOpts.autoplay,
        // Once the user unlocks audio, stop feeding muted:true on every render
        // or componentDidUpdate keeps resolved.muted sticky for the intro.
        muted: introMutedRequest,
        startTime: 0,
        plugins: plugins,
        controlPlane: plane,
        features: {
          subtitles: false,
          continueWatching: false,
          casting: false,
        },
        sessionId: sessionId,
        sessionStartedAt: sessionStartedAt,
      });
    }

    return buildHostPlayerConfig(details, {
      videoFrom: videoFrom,
      isTrailer: isTrailer,
      applySeek: !isTrailer,
      autoplay: true,
      // Prefer unmuted. On reload, never inherit a sticky ngp_muted preference
      // for the initial autoplay request — that turned every refresh into a
      // silent session even when the user had not chosen mute. Mute-retry still
      // handles genuine browser autoplay blocks.
      muted:
        this._sessionAudioUnlocked || this.state.sessionAudioUnlocked
          ? false
          : this.state.forceMutedMainAutoplay
            ? true
            : isReloadNavigation()
              ? false
              : undefined,
      plugins: plugins,
      controlPlane: plane,
      sessionId: sessionId,
      sessionStartedAt: sessionStartedAt,
    });
  }

  renderIntroOverlay(introLayer) {
    if (!this.state.introSkipVisible) return null;
    return (
      <div className="ngp-host-skip">
        <button
          type="button"
          className="ngp-host-skip-btn"
          onClick={this.skipCinematicIntro}
          aria-label="Skip intro"
        >
          Skip Intro
        </button>
      </div>
    );
  }

  render() {
    var pageType = "videoPage";

    if (this.state.onPlayStarted && this.state.videoDetailsFirst) {
      var returnToVideo = this.renderRedirectPage(
        this.state.videoDetailsFirst,
        pageType
      );
      if (returnToVideo != null) {
        return returnToVideo;
      }
    }

    // Deep-link gate redirects (PPV / subscription) before player mounts
    if (this.state.videoDetailsFirst && !this.state.onPlayStarted) {
      var gateRedirect = this.renderRedirectPage(
        this.state.videoDetailsFirst,
        pageType
      );
      if (gateRedirect != null) {
        return gateRedirect;
      }
    }

    if (this.state.loadingFirst) {
      return <ContentLoader />;
    }

    if (this.state.loadError && !this.state.videoDetailsFirst) {
      return (
        <div className="ngp-host-page">
          <div className="ngp-host-stage">
            <div className="ngp-host-back">
              <Link to="/home">
                <img
                  src={window.location.origin + "/assets/img/left-arrow.png"}
                  alt=""
                />
                <span className="txt-overflow capitalize ml-3">Back</span>
              </Link>
            </div>
            <p className="ngp-host-error" role="alert">
              {this.state.loadError}
            </p>
          </div>
        </div>
      );
    }

    var details = this.state.videoDetailsFirst;
    var videoFrom =
      this.props.location.state && this.props.location.state.videoFrom;
    var introLayer =
      (this._controlPlane && this._controlPlane.introLayer) ||
      getPrimaryIntroLayer(
        resolvePlaybackExperience(details, {
          isTrailer: videoFrom === "trailer",
        })
      );
    var showingIntro =
      this.state.playingCinematicIntro && introLayer && introLayer.url;

    var config = this.buildConfig();
    if (showingIntro) {
      config = Object.assign({}, config, {
        source: introLayer.url,
        startTime: 0,
        poster: details.default_image || "",
        title: details.title || details.name || "",
        // Intro is always progressive/HLS on the video element — never embed.
        presentationStrategy: {
          id: "video",
          surfaceKind: "video",
          presentationKind: "video",
          sourceType: "hosted",
          usesVideoElement: true,
          usesAudioElement: false,
          usesCompanionImage: false,
          usesEmbed: false,
          isCollection: false,
          isLive: false,
          defaultCapabilities: ["analytics"],
          adapterHint: "progressive",
        },
      });
    }

    var mediaMeta =
      (this._controlPlane && this._controlPlane.mediaMeta) ||
      resolveMediaFromVideoDetails(details, {
        videoFrom: videoFrom,
      });
    var mediaAsset =
      (this._controlPlane && this._controlPlane.mediaAsset) || null;
    var playableMainSrc = resolvePlayableSrc(mediaMeta, mediaAsset);
    var showTitle =
      this._controlPlane != null
        ? this._controlPlane.showHostTitle
        : true;
    var hasPlayableSrc = !!(
      (showingIntro && introLayer && introLayer.url) ||
      playableMainSrc
    );

    var strategy =
      (config && config.presentationStrategy) ||
      (this._controlPlane && this._controlPlane.strategy) ||
      null;
    var usesCompanion =
      !showingIntro &&
      mediaAsset &&
      mediaAsset.companion &&
      (mediaAsset.companion.image || mediaAsset.companion.audio) &&
      ((strategy && strategy.usesCompanionImage) ||
        mediaAsset.presentationKind === "image_audio" ||
        mediaAsset.presentationKind === "audio" ||
        mediaAsset.sourceType === "companion" ||
        (strategy && strategy.adapterHint === "companion"));
    var companionConfig = usesCompanion ? mediaAsset.companion : null;
    // Companion presentations are playable without a video URL.
    // Never treat companion image URL as a progressive video source.
    if (usesCompanion) {
      hasPlayableSrc = true;
      playableMainSrc = "";
    }

    var playerMode = showingIntro ? "intro" : usesCompanion ? "companion" : "main";
    var playerReactKey =
      String(this.state.playerKey) +
      (showingIntro ? "-intro" : "-main") +
      "-" +
      mediaMeta.contentId;
    if (this._lastPlayerMode !== playerMode + ":" + playerReactKey) {
      tpgTrace("STATE", {
        message: "player_slot",
        mode: playerMode,
        key: playerReactKey,
        hasPlayableSrc: hasPlayableSrc,
        sourcePreview: showingIntro
          ? introLayer && introLayer.url
          : playableMainSrc,
        muted: config && config.muted,
        autoplay: config && config.autoplay,
      });
      this._lastPlayerMode = playerMode + ":" + playerReactKey;
    }

    if (!hasPlayableSrc && !this._loggedMissingSrc) {
      this._loggedMissingSrc = true;
      tpgTrace("ERROR", {
        message: "NextGenPlayer_not_rendered",
        showingIntro: showingIntro,
        playableMainSrc: playableMainSrc,
      });
    }
    if (hasPlayableSrc) {
      this._loggedMissingSrc = false;
    }

    var self = this;
    var stageClass =
      "ngp-host-stage" +
      (this.state.theaterMode ? " ngp-host-stage--theater" : "") +
      (this.state.cinematicHandoff ? " " + CINEMATIC_HANDOFF_CLASS : "");
    return (
      <div className="ngp-host-page">
        <div className={stageClass} ref={this._setStageRef}>
          {showingIntro ? this.renderIntroOverlay(introLayer) : null}
          {this.state.loadError ? (
            <p className="ngp-host-error" role="alert">
              {this.state.loadError}
            </p>
          ) : null}
          {hasPlayableSrc && usesCompanion ? (
            <CompanionPresentationSurface
              key={playerReactKey}
              companion={companionConfig}
              title={details.title || details.name || ""}
              onEvent={function (name, payload) {
                if (self.handlePlaybackEvent) {
                  self.handlePlaybackEvent(name, payload);
                }
              }}
            />
          ) : hasPlayableSrc ? (
          <NextGenPlayer
            key={playerReactKey}
            config={config}
            theaterMode={this.state.theaterMode}
            isCinematicIntro={!!showingIntro}
            onReady={this.handleReady}
            onEnded={this.handleEnded}
            onError={this.handleError}
            onAudioUnlocked={this.handleAudioUnlocked}
            onNeedGestureUnmute={this.handleNeedGestureUnmute}
            onTheaterChange={this.handleTheaterChange}
            onPlaybackEvent={function (name, payload) {
              if (showingIntro) {
                if (
                  name === PlayerEvent.PLAYBACK_ENDED ||
                  name === "ended"
                ) {
                  tpgTrace("STATE", {
                    message: "intro_onPlaybackEvent_ENDED",
                  });
                  self.beginMainContentPlayback();
                  return;
                }
                if (
                  name === PlayerEvent.TIME_UPDATE ||
                  name === "timeupdate"
                ) {
                  var t =
                    (payload && payload.currentTime) ||
                    (self._api &&
                      self._api.getState &&
                      self._api.getState().currentTime) ||
                    0;
                  var dur =
                    (payload && payload.duration) ||
                    (self._api &&
                      self._api.getState &&
                      self._api.getState().duration) ||
                    0;
                  if (
                    introLayer.skip_after_seconds &&
                    t >= introLayer.skip_after_seconds
                  ) {
                    self.setState({ introSkipVisible: true });
                    if (self._session) {
                      self._session.update("intro", { skipVisible: true });
                    }
                  }
                  // Near-end fallback when HTMLMediaElement "ended" is missed
                  if (
                    dur > 1 &&
                    t >= Math.max(0, dur - 0.4) &&
                    self.state.playingCinematicIntro
                  ) {
                    tpgTrace("STATE", {
                      message: "intro_near_end_fallback",
                      currentTime: t,
                      duration: dur,
                    });
                    self.beginMainContentPlayback();
                  }
                }
                return;
              }
              self.handlePlaybackEvent(name, payload);
            }}
          />
          ) : null}
          <div className="ngp-host-back">
            <Link to="/home">
              <img
                src={window.location.origin + "/assets/img/left-arrow.png"}
                alt=""
              />
              {showTitle ? (
                <span className="txt-overflow capitalize ml-3">
                  {mediaMeta.title}
                </span>
              ) : null}
            </Link>
          </div>
          {configuration.get("configData.debug_playback_engine") ? (
            <div className="ngp-host-engine-badge" aria-hidden="true">
              nextgen
            </div>
          ) : null}
        </div>
      </div>
    );
  }
}

export default HostPlaybackPage;
